// src/components/ChatWindow.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, TextField, IconButton, Paper, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import api from "../api/axiosConfig";

/**
 * ✅ Fixed ChatWindow:
 * - Works inside ANY page/phone frame
 * - Prevents input from being covered by bottom nav / mobile browser bars
 * - Uses 100dvh when needed (optional via prop)
 * - Keeps messages scrollable (minHeight: 0)
 */

export default function ChatWindow({
  momentId,
  // If you have a fixed bottom nav (“web icon bar”), set its height here.
  // Example: 64 / 72 / 80
  bottomOffset = 72,

  // If you want ChatWindow to own the viewport (only if you render it alone)
  fullHeight = false,
}) {
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const currentUserId = currentUser?.id;

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  // 🔄 Load messages (polling)
  useEffect(() => {
    if (!momentId) return;

    let cancelled = false;

    const loadMessages = async () => {
      try {
        const res = await api.get(`/messages/${momentId}`);
        if (cancelled) return;
        setMessages(res.data?.messages || []);
      } catch (err) {
        console.error("Failed to load messages", err);
      }
    };

    loadMessages();
    const interval = setInterval(loadMessages, 3000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [momentId]);

  // ✅ Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!momentId) return;
    if (!currentUserId) return;
    if (!text.trim()) return;

    const payload = {
      momentId,
      senderId: currentUserId,
      content: text.trim(),
    };

    const optimistic = {
      id: `tmp-${Date.now()}`,
      content: payload.content,
      senderId: currentUserId,
    };

    setText("");
    setMessages((prev) => [...prev, optimistic]);

    try {
      await api.post("/messages", payload);
    } catch (err) {
      console.error("Send failed", err);
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    }
  };

  return (
    <Box
      sx={{
        // ✅ If ChatWindow is used as the whole screen
        ...(fullHeight
          ? { height: "100dvh", overflow: "hidden" }
          : { flex: 1, minHeight: 0 }),

        display: "flex",
        flexDirection: "column",

        // ✅ Reserve space for your fixed bottom nav (“web icon bar”)
        // If your nav is NOT fixed, set bottomOffset={0}
        pb:
          bottomOffset > 0
            ? `calc(${bottomOffset}px + env(safe-area-inset-bottom))`
            : `env(safe-area-inset-bottom)`,
      }}
    >
      {/* 💬 Messages */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // ✅ allows scrolling area to shrink
          px: 2,
          py: 2,
          overflowY: "auto",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {!currentUserId && (
          <Typography sx={{ color: "#6B7280", fontSize: "0.9rem", mb: 2 }}>
            Please log in again to chat.
          </Typography>
        )}

        {messages.map((msg) => {
          const mine = Number(msg.senderId) === Number(currentUserId);

          return (
            <Box
              key={msg.id}
              sx={{
                display: "flex",
                justifyContent: mine ? "flex-end" : "flex-start",
                mb: 1,
              }}
            >
              <Paper
                sx={{
                  px: 1.5,
                  py: 1,
                  maxWidth: "75%",
                  bgcolor: mine ? "#ECFDF5" : "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  boxShadow: "none",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {msg.content}
              </Paper>
            </Box>
          );
        })}

        <div ref={bottomRef} />
      </Box>

      {/* ✍️ Input */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: "1px solid #E5E7EB",
          display: "flex",
          gap: 1,
          flexShrink: 0,
          bgcolor: "#FFFFFF",

          // ✅ Protect from iOS home indicator / browser bottom UI
          pb: "calc(10px + env(safe-area-inset-bottom))",
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Say something meaningful…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />

        <IconButton
          color="primary"
          disabled={!text.trim() || !currentUserId}
          onClick={handleSend}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
